/**
 * #1006 — international Polymarket's Close, one control for every Open
 * Positions layout (table, card, compact, mini, and their pinned copies), so a
 * position offers the same Close whichever view the trader picked. Before,
 * only the table drew it; the cards had no Close at all.
 *
 * The state is one plain string so a memoized row (`PositionCardRow`, #731)
 * still compares equal when nothing about its Close moved. The click asks the
 * Tracker's one confirm dialog (`setPendingPolyClose`); nothing is sent here.
 */
import { Loader2 } from 'lucide-react';
import { POLY_MIN_ORDER_CONTRACTS } from '../../lib/polymarket/closePosition';

export type PolyCloseState =
  /** Under Polymarket's order minimum: it settles with the market. */
  | 'below-minimum'
  /** This position's close is in flight. */
  | 'closing'
  /** The live feed can't map the position to its outcome token yet. */
  | 'not-loaded'
  /** Another position's close is in flight (one at a time). */
  | 'waiting'
  | 'ready';

/** `closingKey` is the Tracker's in-flight close (`${conditionId}-${side}`), or null. */
export function polyCloseState(
  qty: number,
  loaded: boolean,
  closingKey: string | null,
  key: string,
): PolyCloseState {
  if (qty < POLY_MIN_ORDER_CONTRACTS) return 'below-minimum';
  if (closingKey === key) return 'closing';
  if (!loaded) return 'not-loaded';
  return closingKey !== null ? 'waiting' : 'ready';
}

export function PolyCloseControl({ state, onClose }: { state: PolyCloseState; onClose: () => void }) {
  if (state === 'below-minimum') {
    // Short label, not a dash — the table's Cash Out value already renders
    // '—' when the market isn't loaded, and '— —' reads as a glitch.
    return (
      <span
        className="text-muted-foreground font-mono text-[10px]"
        title={`Below Polymarket's ${POLY_MIN_ORDER_CONTRACTS}-contract minimum — settles with the market.`}
      >
        &lt;{POLY_MIN_ORDER_CONTRACTS}
      </span>
    );
  }
  // A greyed Close says why it waits (as the US Close does), never still offering the sell.
  const title = state === 'not-loaded' ? 'Market not loaded in the live feed — open its sport tab to enable Close.'
    : state === 'closing' ? 'This position’s close is in flight.'
      : state === 'waiting' ? 'Close waits while another position’s close finishes.'
        : 'Sell this position at market (fill-and-kill sweep — nothing rests)';
  return (
    // The cards open the order panel on click; Close is its own action, and
    // a click on the greyed button must not open the panel either, so the
    // wrapper stops it (and carries the reason for hover, like the button).
    <span className="inline-flex shrink-0" onClick={(e) => e.stopPropagation()} title={title}>
      <button
        type="button"
        disabled={state !== 'ready'}
        onClick={onClose}
        data-testid="poly-position-close"
        className="rounded border border-red-500/40 px-2 py-0.5 text-[10px] font-medium text-red-400 light:text-red-700 hover:bg-red-500/10 disabled:opacity-50"
        title={title}
      >
        {state === 'closing' ? <Loader2 size={12} className="inline animate-spin" /> : 'Close'}
      </button>
    </span>
  );
}
